export const isVoiceMessage = (message: any): boolean => {
  if (!message) return false;

  // If string: only consider it voice if it looks like a file path / URL containing 'voice_message_'
  // NOT a multi-word message text or code
  if (typeof message === 'string') {
    const lower = message.trim().toLowerCase();
    if (!lower.includes('voice_message_')) return false;
    if (lower.includes('\n') || /^(if\s*\(|const\s|let\s|var\s|function\s|class\s|import\s)/.test(lower)) {
      return false;
    }
    return true;
  }

  if (Array.isArray(message)) return message.some(isVoiceMessage);

  if (typeof message !== 'object') return false;

  // 1. Explicit voice flags in meta or message root
  const meta = getMessageMeta(message);
  if (meta.isVoice || meta.voiceMessage || message.isVoice || message.voiceMessage) return true;
  if (meta.mediaType === 'voice' || meta.type === 'voice' || message.type === 'voice' || message.mediaType === 'voice') return true;

  // 2. Candidate attachment URLs and file names ONLY (never message text/content)
  const candidateUrls: any[] = [
    message.fileUrl,
    message.url,
    message.audioUrl,
    message.voiceUrl,
    meta.fileUrl,
    meta.url,
    meta.audioUrl,
    meta.voiceUrl,
    meta.ogFileName,
    meta.fileName,
    meta.name
  ];

  if (message.file) {
    if (typeof message.file === 'string') candidateUrls.push(message.file);
    else if (typeof message.file === 'object') {
      candidateUrls.push(message.file.url, message.file.fileUrl, message.file.name, message.file.fileName);
    }
  }

  const rawFiles = message.files || meta.files;
  if (Array.isArray(rawFiles)) {
    for (const f of rawFiles) {
      if (!f) continue;
      if (typeof f === 'string') candidateUrls.push(f);
      else if (typeof f === 'object') {
        candidateUrls.push(f.url, f.fileUrl, f.name, f.fileName);
      }
    }
  }

  const hasVoiceUrl = candidateUrls.some(u => typeof u === 'string' && u.toLowerCase().includes('voice_message_'));
  if (hasVoiceUrl) return true;

  // 3. Exact placeholder emitted by voice recorder with audio/duration
  const rawMsg = typeof message.message === 'string' ? message.message.trim() : '';
  const hasAudioMeta = meta.fileType?.startsWith('audio/') || meta.mediaType === 'audio' || message.type === 'audio';
  const hasDuration = !!(meta.duration || message.duration);
  if (rawMsg === '🎤 Voice message' && (hasAudioMeta || hasDuration || candidateUrls.some(u => typeof u === 'string' && u.trim() !== ''))) {
    return true;
  }

  return false;
};

export const isImage = (url: string | any): boolean => {
  if (!url) return false;
  const target = Array.isArray(url) ? url[0] : url;
  if (!target || typeof target !== 'string') return false;
  return /\.(jpg|jpeg|png|gif|webp|avif|svg)(\?.*)?$/i.test(target);
};

export const isVideo = (url: string | any): boolean => {
  if (!url) return false;
  const target = Array.isArray(url) ? url[0] : url;
  if (!target || typeof target !== 'string') return false;
  if (isVoiceMessage(target)) return false;
  return /\.(mp4|webm|ogg|mov|avi|mkv)(\?.*)?$/i.test(target);
};

export const isAudio = (url: string | any, name?: string): boolean => {
  if (isVoiceMessage(url) || (name && isVoiceMessage(name))) return true;
  if (!url) return false;
  const target = Array.isArray(url) ? url[0] : url;
  if (!target || typeof target !== 'string') return false;
  return /\.(mp3|wav|ogg|m4a|flac|aac)(\?.*)?$/i.test(target);
};

import { DOCUMENT_EXTENSIONS } from '../../../constants/chat.constant';
export { DOCUMENT_EXTENSIONS };

export const isValidFileType = (file: File): boolean => {
  if (!file || !file.name) return false;
  const fileName = file.name.toLowerCase();
  const fileType = (file.type || '').toLowerCase();

  // Check for documents FIRST (aligned with DOCUMENT_EXTENSIONS)
  // This must be checked before fileType.startsWith('video/') because
  // Chromium assigns MIME type 'video/mp2t' to TypeScript (.ts) files!
  if (DOCUMENT_EXTENSIONS.some(ext => fileName.endsWith(ext.toLowerCase()))) {
    return true;
  }

  // Check for images
  if (fileType.startsWith('image/')) {
    return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(fileName);
  }

  // Check for videos
  if (fileType.startsWith('video/')) {
    return /\.(mp4|mov|avi|mkv|webm)$/i.test(fileName);
  }

  // Check for audio
  if (fileType.startsWith('audio/')) {
    return /\.(mp3|wav|aac|ogg|flac)$/i.test(fileName);
  }

  return false;
};

export const isDocument = (url: string | any): boolean => {
  if (!url) return false;
  const target = Array.isArray(url) ? url[0] : url;
  if (!target || typeof target !== 'string') return false;
  if (isVoiceMessage(target)) return false;
  const cleanTarget = target.split('?')[0].toLowerCase();
  if (DOCUMENT_EXTENSIONS.some(ext => cleanTarget.endsWith(ext.toLowerCase()))) {
    return true;
  }
  return /\.(doc|docx|pdf|zip|xml|rar|xlsx|pptx|csv|txt|7z|tar|gz|tgz|json|ts|js|py|html|css|sql)(\?.*)?$/i.test(target);
};

export const isURL = (str: string): boolean => {
  const urlRegex = /^(https?:\/\/)([^\s$.?#].[^\s]*)$/i;
  return typeof str === 'string' && urlRegex.test(str.trim());
};

export const extractUrls = (text: string): string[] => {
  if (!text || typeof text !== 'string') return [];
  const matches = text.match(/https?:\/\/[^\s]+/g);
  if (!matches) return [];
  return matches.map(url => {
    let clean = url;
    const punctuation = [',', '.', '!', '?', ')', ']', '}'];
    while (clean.length > 0 && punctuation.includes(clean[clean.length - 1])) {
      if (clean[clean.length - 1] === ')' && clean.includes('(')) break;
      if (clean[clean.length - 1] === ']' && clean.includes('[')) break;
      if (clean[clean.length - 1] === '}' && clean.includes('{')) break;
      clean = clean.substring(0, clean.length - 1);
    }
    return clean.replace(/\/+$/, '');
  }).filter(Boolean);
};

export const getMessageMeta = (message: any): any => {
  if (!message) return {};
  let meta = message.meta || (message.data && message.data.meta);
  if (typeof meta === 'string') {
    try {
      meta = JSON.parse(meta);
    } catch (e) {
      return {};
    }
  }
  return meta || {};
};

export const isGifMessage = (message: any): boolean => {
  if (!message) return false;
  const meta = getMessageMeta(message);
  if (meta.mediaType === 'gif' || meta.gifUrl || meta.media_type === 'gif') return true;

  const url = message.url || meta.url || meta.fileUrl || meta.gifUrl;
  const actualUrl = Array.isArray(url) ? url[0] : url;

  if (typeof actualUrl !== 'string') return false;
  const basePurl = actualUrl.split('?')[0];
  return basePurl.toLowerCase().endsWith('.gif') || basePurl.toLowerCase().includes('giphy.com') || basePurl.toLowerCase().includes('tenor.com');
};

export const getFileType = (filename: any): string => {
  if (!filename) return 'application/octet-stream';
  const target = Array.isArray(filename) ? filename[0] : filename;
  if (!target || typeof target !== 'string') return 'application/octet-stream';
  const extension = target.split('?')[0].split('.').pop()?.toLowerCase() || '';
  const mapping: { [key: string]: string } = {
    // Images
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',

    // Video
    'mp4': 'video/mp4',
    'mov': 'video/quicktime',
    'avi': 'video/x-msvideo',
    'mkv': 'video/x-matroska',
    'webm': 'video/webm',
    'ogg': 'video/ogg',

    // Audio
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'aac': 'audio/aac',
    'flac': 'audio/flac',

    // Documents
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'txt': 'text/plain',
    'rtf': 'application/rtf',
    'odt': 'application/vnd.oasis.opendocument.text',
    'csv': 'text/csv',
    'tsv': 'text/tab-separated-values',

    // Code & Web files
    'js': 'application/javascript',
    'ts': 'text/typescript',
    'jsx': 'text/jsx',
    'tsx': 'text/typescript-jsx',
    'json': 'application/json',
    'py': 'text/x-python',
    'java': 'text/x-java-source',
    'html': 'text/html',
    'htm': 'text/html',
    'css': 'text/css',
    'scss': 'text/x-scss',
    'sass': 'text/x-sass',
    'less': 'text/x-less',
    'sql': 'application/sql',
    'c': 'text/x-c',
    'cpp': 'text/x-c++',
    'h': 'text/x-c',
    'hpp': 'text/x-c++',
    'cs': 'text/x-csharp',
    'php': 'text/x-php',
    'rb': 'text/x-ruby',
    'go': 'text/x-go',
    'rs': 'text/x-rust',
    'swift': 'text/x-swift',
    'kt': 'text/x-kotlin',
    'kts': 'text/x-kotlin',
    'sh': 'application/x-sh',
    'bash': 'application/x-sh',
    'xml': 'application/xml',
    'yaml': 'text/yaml',
    'yml': 'text/yaml',
    'md': 'text/markdown',
    'env': 'text/plain',
    'angular': 'application/octet-stream',
    'git': 'application/octet-stream',
    'gitignore': 'text/plain',
    'dart': 'application/vnd.dart',
    'vue': 'text/x-vue',
    'toml': 'application/toml',
    'ini': 'text/plain',

    // Archives
    'zip': 'application/zip',
    'rar': 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed',
    'tar': 'application/x-tar',
    'gz': 'application/gzip',
    'tgz': 'application/gzip',

    // Design
    'psd': 'image/vnd.adobe.photoshop',
    'ai': 'application/postscript',
    'eps': 'application/postscript',
    'fig': 'application/octet-stream',
    'sketch': 'application/octet-stream'
  };
  return mapping[extension] || 'application/octet-stream';
};

export const getFileExtLabel = (fileNameOrUrl: any): string => {
  if (!fileNameOrUrl) return 'file';
  const target = Array.isArray(fileNameOrUrl) ? fileNameOrUrl[0] : fileNameOrUrl;
  if (!target || typeof target !== 'string') return 'file';
  const clean = target.split('?')[0];
  const parts = clean.split('.');
  if (parts.length > 1) {
    const ext = parts.pop()?.toLowerCase() || '';
    if (ext.length <= 5) return ext;
    return ext.slice(0, 4);
  }
  return 'file';
};

export const getFileBadgeColor = (fileNameOrUrl: any): string => {
  const ext = getFileExtLabel(fileNameOrUrl).toLowerCase();
  // Code & Markup files: Telegram vibrant blue #2481cc
  if ([
    'ts', 'js', 'jsx', 'tsx', 'json', 'py', 'java', 'html', 'htm', 'css',
    'scss', 'sass', 'less', 'sql', 'c', 'cpp', 'h', 'hpp', 'cs', 'php',
    'rb', 'go', 'rs', 'swift', 'kt', 'kts', 'sh', 'bash', 'xml', 'yaml',
    'yml', 'md', 'env', 'vue', 'dart', 'toml', 'ini', 'git', 'angular', 'gitignore'
  ].includes(ext)) {
    return '#2481cc';
  }
  // PDF: Red
  if (ext === 'pdf') {
    return '#e53935';
  }
  // Spreadsheets / Data: Green
  if (['xls', 'xlsx', 'csv', 'tsv'].includes(ext)) {
    return '#2e7d32';
  }
  // Word & Text Documents: Deep Blue
  if (['doc', 'docx', 'txt', 'rtf', 'odt'].includes(ext)) {
    return '#1e88e5';
  }
  // Presentations: Orange
  if (['ppt', 'pptx'].includes(ext)) {
    return '#f4511e';
  }
  // Archives: Amber / Golden Orange
  if (['zip', 'rar', '7z', 'tar', 'gz', 'tgz'].includes(ext)) {
    return '#fb8c00';
  }
  // Design & Vector: Purple
  if (['psd', 'ai', 'eps', 'fig', 'sketch', 'svg'].includes(ext)) {
    return '#8e24aa';
  }
  // Default fallback: Telegram Slate Blue
  return '#5c6bc0';
};

export const stripTimestampPrefix = (filename: any): string => {
  if (!filename || typeof filename !== 'string') return '';
  let name = filename.trim();

  // If a URL or path was passed, get just the filename part
  if (name.includes('/') || name.includes('\\')) {
    const parts = name.split(/[/\\]/);
    name = parts[parts.length - 1];
  }

  // Strip query parameters
  if (name.includes('?')) {
    name = name.split('?')[0];
  }

  try {
    name = decodeURIComponent(name);
  } catch (e) {}

  // Strip prepended numbers/timestamps followed by '-' or '_' (e.g. "1792761016129-inventories.component.ts")
  const stripped = name.replace(/^(\d+[-_]+)+/, '');
  return stripped || name;
};

export const getCleanFileName = (nameOrUrl: any, url?: string): string => {
  let actualUrl = url || nameOrUrl;
  let name = url ? nameOrUrl : null;

  if (name && typeof name === 'string' && name.trim() !== '' && !isURL(name)) {
    return stripTimestampPrefix(name);
  }
  if (Array.isArray(actualUrl)) actualUrl = actualUrl[0];
  if (!actualUrl || typeof actualUrl !== 'string') return "File";

  try {
    const parts = actualUrl.split(/[/\\]/);
    const lastPart = parts[parts.length - 1];
    const filename = lastPart.split('?')[0];
    const clean = stripTimestampPrefix(filename);
    return clean || "File";
  } catch (e) {
    return "File";
  }
};



export const getTimeAgo = (date: any): string => {
  if (!date) return "";
  const now = new Date();
  const then = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (diffInSeconds < 60) return "Just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

  return then.toLocaleDateString();
};

export const isSystemMessage = (msg: any): boolean => {
  if (!msg) return false;
  const meta = msg.meta || {};

  return !!(
    msg.isSystemMessage ||
    msg.type === 'system' ||
    msg.type === 'finance' ||
    meta.type === 'system' ||
    meta.type === 'finance' ||
    meta.module === 'finance' ||
    meta.module === 'lead-tracking' ||
    meta.module === 'lead-track' ||
    meta.subType ||
    meta.pinnedBy
  );
};

export const isGroupActionMessage = (msg: any): boolean => {
  if (!msg) return false;
  const meta = msg.meta || {};
  const subType = (meta.subType || meta.event || meta.action || '').toLowerCase().trim();
  const type = (meta.type || msg.type || '').toLowerCase().trim();

  // 1. Explicit pin notifications
  if (
    msg.isPinNotification ||
    meta.isPin ||
    type === 'pin' ||
    subType === 'pin'
  ) {
    return true;
  }

  // 2. Explicit group action subTypes
  const groupSubtypes = [
    'admin assigned',
    'admin added',
    'admin removed',
    'admin changed',
    'group creation',
    'group created',
    'member added',
    'user added',
    'group member removed',
    'member removed',
    'user removed',
    'member left',
    'user left',
    'group name changed',
    'group subject changed',
    'group description changed',
    'group icon changed',
    'group avatar changed',
    'group photo changed',
    'group image changed',
    'message pinned',
    'message unpinned',
    'group cleared',
    'group deleted',
    'group settings changed',
    'group permissions changed'
  ];

  if (groupSubtypes.some(st => subType === st || subType.includes(st))) {
    return true;
  }

  // 3. SubType includes admin/member/group/pinned/unpinned
  if (
    subType.includes('admin') ||
    subType.includes('member') ||
    subType.includes('group') ||
    subType.includes('pinned') ||
    subType.includes('unpinned')
  ) {
    return true;
  }

  // 4. Check all message text variations
  const textsToCheck = [
    typeof msg === 'string' ? msg : '',
    msg.message,
    msg.systemText,
    msg.text
  ].filter(t => typeof t === 'string' && t.trim() !== '');

  for (const raw of textsToCheck) {
    const lower = raw.toLowerCase().trim();

    // Do not treat business alerts (which start with Alert: or have specific lead/finance formats) as group actions
    if (lower.startsWith('alert:') || meta.leadId || meta.module === 'lead-tracking' || meta.module === 'finance') {
      continue;
    }

    // Admin action messages
    if (
      lower.includes('admin') ||
      /\b(?:is|are)\s+now\s+an\s+admin\b/i.test(lower) ||
      /\bbecame\s+an\s+admin\b/i.test(lower) ||
      /\b(?:is|are)\s+no\s+longer\s+an\s+admin\b/i.test(lower) ||
      /\bdismissed\s+as\s+admin\b/i.test(lower) ||
      /\bremoved\s+as\s+admin\b/i.test(lower) ||
      /\bmade\s+admin\b/i.test(lower)
    ) {
      return true;
    }

    // Group action messages
    if (
      lower.includes('created the group') ||
      lower.includes('created group') ||
      lower.includes('changed the group') ||
      lower.includes('changed group') ||
      lower.includes('added') ||
      lower.includes('removed') ||
      lower.includes('left the group') ||
      lower.includes('left') ||
      lower.includes('joined the group') ||
      lower.includes('joined') ||
      lower.includes('cleared chat') ||
      lower.includes('deleted group') ||
      lower.includes('group deleted') ||
      lower.includes('pinned') ||
      lower.includes('unpinned') ||
      lower.includes('group icon') ||
      lower.includes('group photo') ||
      lower.includes('group description') ||
      lower.includes('group name') ||
      lower.includes('group settings')
    ) {
      return true;
    }
  }

  return false;
};

export const isMessageRedundantWithFile = (message: any): boolean => {
  if (!message) return false;
  const rawMsg = message.meta?.caption ?? (typeof message.message === 'string' ? message.message : (message.message?.text || message.message?.message || message.message?.content || message.text || message.content || ''));
  const msg = typeof rawMsg === 'string' ? rawMsg.trim() : '';
  if (!msg) return true;

  const urls: string[] = [];
  const addUrl = (u: any) => {
    if (!u) return;
    if (Array.isArray(u)) u.forEach(addUrl);
    else if (typeof u === 'string') urls.push(u);
    else if (typeof u === 'object' && (u.fileUrl || u.url)) addUrl(u.fileUrl || u.url);
  };

  addUrl(message.url);
  addUrl(message.file);
  if (message.meta) {
    addUrl(message.meta.url);
    addUrl(message.meta.fileUrl);
    addUrl(message.meta.file_url);
    addUrl(message.meta.files);
    addUrl(message.meta.gifUrl);
  }
  addUrl(message.files);

  const lowerMsg = msg.toLowerCase().trim();

  const placeholders = [
    'sent a gif', 'sent an gif', 'gif', 'sont a gif',
    'sent a photo', 'sent a video', 'sent an audio',
    'sent a document', 'photo', 'video', 'audio', 'document',
    'sent an attachment', 'attachment', 'sent a file', 'file',
    'sent an image', 'image', 'sent an video', 'sent an document',
    'sent an photo', 'sent an audio', 'sent a video', 'sent a document',
    'sent a photo', 'sent a audio', 'voice message', '🎤 voice message'
  ];

  if (placeholders.some(p => lowerMsg === p)) {
    if (getReplyThumbnail(message) || getFilesFromMessage(message).length > 0) return true;
  }

  // Clean message: remove ALL leading digits-separators like '12345-6789_'
  const cleanMsg = lowerMsg.replace(/^(\d+[-_])+/g, '');

  return urls.some(u => {
    const lowerU = typeof u === 'string' ? u.toLowerCase() : '';
    const filenameFromUrl = lowerU.split('/').pop() || '';

    // 1. Direct match or substring match (e.g. filename in URL)
    if (lowerU.includes(lowerMsg) || lowerMsg.includes(filenameFromUrl)) return true;

    // 2. Cleaned match (ignoring timestamp prefixes)
    const cleanU = filenameFromUrl.replace(/^(\d+[-_])+/g, '');
    if (cleanU && cleanMsg && (cleanU === cleanMsg || cleanU.includes(cleanMsg) || cleanMsg.includes(cleanU))) return true;

    // 3. Fallback: if message is long and looks like a URL part, or if they share a significant common part
    if (cleanMsg.length > 5 && cleanU.includes(cleanMsg)) return true;

    return false;
  });
};

export const getReplyMediaType = (msg: any): string => {
  if (!msg) return 'Message';

  // Robust check for star records
  const target = (msg.message && typeof msg.message === 'object') ? msg.message : msg;
  const meta = getMessageMeta(target);

  const rawUrl = target.url || target.meta?.url || target.meta?.fileUrl || target.meta?.file_url;
  const url = Array.isArray(rawUrl)
    ? (typeof rawUrl[0] === 'string' ? rawUrl[0] : (rawUrl[0]?.fileUrl || rawUrl[0]?.url || ''))
    : (typeof rawUrl === 'string' ? rawUrl : (rawUrl?.fileUrl || rawUrl?.url || ''));

  const files = getFilesFromMessage(msg);

  if (isVoiceMessage(msg)) return 'Voice Message';

  if (meta.mediaType === 'gif') return 'GIF';

  if (isDocument(url)) return 'Document';
  if (files.length > 0 && isDocument(files[0]?.fileUrl || files[0]?.url || files[0]?.name)) return 'Document';

  if (meta.contentType === 'video' || (meta.fileType?.startsWith('video/') && !isDocument(url))) return 'Video';
  if (meta.contentType === 'audio' || meta.fileType?.startsWith('audio/')) return 'Audio';

  if (isImage(url)) return 'Photo';
  if (isVideo(url)) return 'Video';
  if (isAudio(url)) return 'Audio';
  if (isDocument(url)) return 'Document';

  if (files.length > 0) {
    const file = files[0];
    const fUrl = file?.fileUrl || file?.url || '';
    if (file.type?.startsWith('image/') || isImage(fUrl)) return 'Photo';
    if (file.type?.startsWith('video/') || isVideo(fUrl)) return 'Video';
    if (file.type?.startsWith('audio/') || isAudio(fUrl)) return 'Audio';
    return 'Document';
  }

  if (target.meta?.fileType) {
    if (target.meta.fileType.startsWith('image/')) return 'Photo';
    if (target.meta.fileType.startsWith('video/')) return 'Video';
    if (target.meta.fileType.startsWith('audio/')) return 'Audio';
    return 'Document';
  }

  if (typeof target.message === 'string' && isURL(target.message)) {
    if (isImage(target.message)) return 'Photo';
    if (isVideo(target.message)) return 'Video';
    if (isAudio(target.message)) return 'Audio';
    if (isDocument(target.message)) return 'Document';
  }

  return 'Message';
};


export const getReplyMediaTypeIcon = (msg: any): string => {
  if (!msg) return '';
  const type = getReplyMediaType(msg);
  switch (type) {
    case 'Photo': return 'assets/camera.png';
    case 'Video': return 'assets/video-camera.png';
    case 'Audio': return 'assets/headphones.png';
    case 'Document': return 'assets/fileAttachment.png';
    case 'GIF': return 'assets/gif.png';
    case 'Voice Message': return '';
    default: return '';
  }
};

export const cleanReplyText = (rawText: string): string => {
  if (!rawText || typeof rawText !== 'string') return '';
  let cleaned = rawText.trim();

  // If text contains HTML tags (e.g. email templates, rich text, <html>, <div>, <table>, etc.)
  if (/<[a-z!/][\s\S]*>/i.test(cleaned)) {
    // 1. Remove doctype, head, style, script, svg blocks entirely
    cleaned = cleaned
      .replace(/<!DOCTYPE[^>]*>/gi, '')
      .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<svg[^>]*>[\s\S]*?<\/svg>/gi, '');

    // 2. Convert block-level tags and line-breaks to spaces
    cleaned = cleaned.replace(/<(br|\/p|\/div|\/tr|\/li|hr|h[1-6]|\/h[1-6])[^>]*>/gi, ' ');

    // 3. Strip all remaining HTML tags
    cleaned = cleaned.replace(/<[^>]+>/g, ' ');

    // 4. Decode common HTML entities
    cleaned = cleaned
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'");
  }

  // Strip blockquote markdown markers (> or >>) at line starts so preview displays as normal text
  cleaned = cleaned.replace(/(?:^|\r?\n)\s*>+\s?/g, (match) => (match.startsWith('\n') || match.startsWith('\r')) ? ' ' : '');

  // 5. Flatten multiple whitespace and newlines to a single space
  cleaned = cleaned.replace(/\s+/g, ' ').trim();

  // 6. If cleaned text is empty (e.g. input was only tags or spaces), fallback to original without newlines
  if (!cleaned && rawText.trim()) {
    cleaned = rawText.replace(/\s+/g, ' ').trim();
  }

  return cleaned;
};

export const getReplyMessageText = (msg: any): string => {
  if (!msg) return '';

  const target = (msg.message && typeof msg.message === 'object') ? msg.message : msg;
  let rawText = '';
  if (typeof target.message === 'string') {
    rawText = target.message;
  } else if (typeof msg.message === 'string') {
    rawText = msg.message;
  } else if (typeof target.text === 'string') {
    rawText = target.text;
  } else if (typeof target.content === 'string') {
    rawText = target.content;
  } else if (typeof target.preview === 'string') {
    rawText = target.preview;
  }

  if (!rawText && (target.meta?.caption || msg.meta?.caption)) {
    rawText = target.meta?.caption || msg.meta?.caption || '';
  }

  const cleanedText = cleanReplyText(rawText);

  if (!cleanedText && (target.meta?.title || msg.meta?.title)) {
    return target.meta?.title || msg.meta?.title;
  }

  const mediaType = getReplyMediaType(msg);
  if (!cleanedText || isMessageRedundantWithFile(msg)) {
    if (mediaType === 'Voice Message') {
      const duration = target.meta?.duration || target.duration || msg.meta?.duration || msg.duration;
      if (duration) {
        const minutes = Math.floor(duration / 60);
        const seconds = Math.floor(duration % 60);
        return `voice message (${minutes}:${seconds.toString().padStart(2, '0')})`;
      }
      return 'voice message';
    }
    return mediaType;
  }

  return cleanedText;
};

export const getReplyThumbnail = (msg: any): string | null => {
  if (!msg) return null;
  if (getReplyMediaType(msg) === 'Message') return null;

  // Robust check for star records
  const target = (msg.message && typeof msg.message === 'object') ? msg.message : msg;
  const meta = getMessageMeta(target);

  const rawUrl = target.url || target.file || target.meta?.url || target.meta?.fileUrl || target.meta?.file_url;
  const url = Array.isArray(rawUrl)
    ? (typeof rawUrl[0] === 'string' ? rawUrl[0] : (rawUrl[0]?.fileUrl || rawUrl[0]?.url || ''))
    : (typeof rawUrl === 'string' ? rawUrl : (rawUrl?.fileUrl || rawUrl?.url || ''));

  const files = getFilesFromMessage(msg);

  if (isVoiceMessage(msg)) return null;

  if (meta?.mediaType === 'gif' && (meta.gifUrl || target.url)) return meta.gifUrl || (typeof target.url === 'string' ? target.url : null);
  if (meta?.image && typeof meta.image === 'string') return meta.image;
  if (meta?.thumbnailUrl && typeof meta.thumbnailUrl === 'string') return meta.thumbnailUrl;
  if (meta?.thumbnail_url && typeof meta.thumbnail_url === 'string') return meta.thumbnail_url;
  if (meta?.thumbnail && typeof meta.thumbnail === 'string') return meta.thumbnail;
  if (meta?.thumb && typeof meta.thumb === 'string') return meta.thumb;

  if (isImage(url)) return url;

  if (files.length > 0) {
    const file = files[0];
    const fUrl = file.fileUrl || file.url || '';
    if (fUrl && (file.type?.startsWith('image/') || isImage(fUrl))) return fUrl;
    if (file.type?.startsWith('video/') || isVideo(fUrl)) return 'assets/video-file.jpg';
    if (file.type?.startsWith('audio/') || isAudio(fUrl)) return 'assets/audio-thumbnail.png';
    if (file.type?.startsWith('application/') || isDocument(fUrl) || file.name) {
      const docNameOrUrl = (typeof fUrl === 'string' && fUrl) ? fUrl : (typeof file.name === 'string' ? file.name : '');
      if (docNameOrUrl) {
        const lowerDoc = docNameOrUrl.toLowerCase();
        if (lowerDoc.endsWith('.pdf')) return 'assets/pdf.png';
        if (lowerDoc.match(/\.(doc|docx)$/i)) return 'assets/docx-img.png';
        if (lowerDoc.match(/\.(xls|xlsx)$/i)) return 'assets/excel.png';
        if (lowerDoc.match(/\.(ppt|pptx)$/i)) return 'assets/ppt-file-img.png';
      }
      return 'assets/docs.png';
    }
  }

  if (isVideo(url) || (meta?.fileType?.startsWith('video/'))) {
    return 'assets/video-file.jpg';
  }

  if (isDocument(url) || (meta?.fileType && !meta.fileType.startsWith('image/') && !meta.fileType.startsWith('video/') && !meta.fileType.startsWith('audio/'))) {
    if (url && typeof url === 'string') {
      const lowerUrl = url.toLowerCase();
      if (lowerUrl.endsWith('.pdf')) return 'assets/pdf.png';
      if (lowerUrl.match(/\.(doc|docx)$/i)) return 'assets/docx-img.png';
      if (lowerUrl.match(/\.(xls|xlsx)$/i)) return 'assets/excel.png';
      if (lowerUrl.match(/\.(ppt|pptx)$/i)) return 'assets/ppt-file-img.png';
    }
    return 'assets/docs.png';
  }

  if (isAudio(url) || meta?.fileType?.startsWith('audio/')) {
    return 'assets/audio-thumbnail.png';
  }

  if (isImage(target.message)) return target.message;

  return null;
};


export const getAggregatedReactions = (reactions: any[]): any[] => {
  if (!reactions || reactions.length === 0) return [];
  const reactionMap = new Map<string, { emoji: string; count: number; userIds: string[] }>();
  reactions.forEach((reaction: any) => {
    let emoji = reaction;
    if (typeof reaction === 'object' && reaction !== null) {
      emoji = reaction.emoji || reaction.emote || '';
    }
    if (!emoji) return;
    const userId = reaction.user_id || reaction.userId || reaction.sender_id;
    if (!reactionMap.has(emoji)) {
      reactionMap.set(emoji, { emoji, count: 0, userIds: [] });
    }
    const aggReaction = reactionMap.get(emoji)!;
    aggReaction.count++;
    if (userId && !aggReaction.userIds.includes(userId)) {
      aggReaction.userIds.push(userId);
    }
  });
  return Array.from(reactionMap.values()).sort((a, b) => b.count - a.count);
};

export const isValidImageUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false;
  if (/\.(jpg|jpeg|png|gif|bmp|webp|svg|ico)(\?|$)/i.test(url)) return true;
  return /^https?:\/\/.+/.test(url);
};



export const getFilesFromMessage = (message: any): any[] => {
  if (!message || isGifMessage(message)) return [];

  // Robust check: star records often nest the original message. 
  // We prefer the object that actually has file/meta data.
  const hasData = (m: any) => m && (
    (m.files && m.files.length > 0) ||
    (m.meta?.files && m.meta.files.length > 0) ||
    m.url || m.fileUrl || m.file || m.msg ||
    m.meta?.fileUrl || m.meta?.url || m.meta?.file_url
  );
  const target = (message.message && typeof message.message === 'object' && hasData(message.message))
    ? message.message
    : message;

  if (target._processedFiles) return target._processedFiles;

  let files: any[] = [];
  // A forwarded carousel is stored as ONE message carrying every file in meta.files;
  // history loaders rebuild `files` from its single `url`, so prefer the longer list.
  const metaFiles = target.meta?.files || [];
  if (metaFiles.length > (target.files?.length || 0)) {
    files = metaFiles;
  } else if (target.files && target.files.length > 0) {
    files = target.files;
  } else {
    const url = target.url || target.meta?.url;
    if (Array.isArray(url) && url.length > 0) {
      const meta = getMessageMeta(target);
      files = url.map((u, i) => ({
        fileUrl: typeof u === 'string' ? u : (u?.fileUrl || u?.url || ''),
        type: isImage(u) ? 'image/jpeg' : isVideo(u) ? 'video/mp4' : isAudio(u) ? 'audio/mpeg' : (isDocument(u) ? getFileType(u) : (meta.fileType || getFileType(u))),
        name: (i === 0 ? meta.ogFileName : null) || getCleanFileName(u)
      }));
    } else {
      const singleUrl = target.url || target.fileUrl || target.file || target.meta?.fileUrl || target.meta?.url || target.meta?.file_url;
      if (singleUrl && (typeof singleUrl === 'string' ? (singleUrl.startsWith('http') || singleUrl.includes('voice_message_')) : (singleUrl.fileUrl || singleUrl.url))) {
        const meta = getMessageMeta(target);
        const resolvedUrl = typeof singleUrl === 'string' ? singleUrl : (singleUrl.fileUrl || singleUrl.url);
        files = [{
          fileUrl: resolvedUrl,
          type: meta.fileType || getFileType(resolvedUrl),
          name: meta.ogFileName || getCleanFileName(resolvedUrl)
        }];
      }
    }
  }


  const processed = files.map((f, i) => {
    const url = typeof f === 'string' ? f : (f?.fileUrl || f?.url);
    let fileType = (typeof f === 'object' && f?.type) ? f.type : 'application/octet-stream';

    if (url && (fileType === 'application/octet-stream' || !fileType || fileType.startsWith('video/'))) {
      if (isDocument(url)) fileType = getFileType(url);
      else if (isVoiceMessage(url)) fileType = 'audio/mpeg';
      else if (isVideo(url)) fileType = 'video/mp4';
      else if (isImage(url)) fileType = 'image/jpeg';
      else if (isAudio(url)) fileType = 'audio/mpeg';
    }

    if (isVoiceMessage(url)) fileType = 'audio/mpeg';

    const meta = getMessageMeta(message);
    const rawFileName = (typeof f === 'object' && (f?.ogFileName || f?.name))
      ? (f.ogFileName || f.name)
      : ((i === 0 ? meta.ogFileName : null) || getCleanFileName(url));
    const fileName = stripTimestampPrefix(rawFileName);

    return {
      ...(typeof f === 'object' ? f : {}),
      type: fileType,
      fileUrl: url,
      name: fileName,
      size: (typeof f === 'object' && (f?.size || f?.fileSize)) ? (f.size || f.fileSize) : (i === 0 ? (meta.size || meta.fileSize) : undefined)
    };
  });

  message._processedFiles = processed;
  return processed;
};

/**
 * Detects whether a text message contains HTML tags, XML, or multi-line code constructs.
 */
export const isCodeOrHtml = (text: string): boolean => {
  if (!text || typeof text !== 'string') return false;
  const trimmed = text.trim();
  if (!trimmed) return false;

  // 1. Markdown code blocks or backtick expressions
  if (trimmed.startsWith('```') || trimmed.includes('```')) {
    return true;
  }

  // 2. HTML/XML tags (standard or escaped with leading backslash like \<tag>)
  // Matches tags like <tr>, <div>, <table ...>, </p>, <!DOCTYPE ...>, <!-- ... -->
  const htmlTagPattern = /\\?<\/?([a-zA-Z][\w:-]*|\!DOCTYPE|!--)(?:\s+[^>]*)?\/?>/i;
  if (htmlTagPattern.test(trimmed)) {
    return true;
  }

  // 3. HTML entities representing tags e.g. &lt;div&gt;
  if (/&lt;\/?([a-zA-Z][\w:-]*)(?:\s+[^&]*)?&gt;/i.test(trimmed)) {
    return true;
  }

  // 4. Common programming patterns across multiple lines or starting with code keywords
  const codePattern = /^(import\s+.*from|export\s+(default\s+)?(class|function|const|let|var|interface|type)|(public\s+|private\s+|protected\s+)?(class|interface|enum)\s+\w+|(async\s+)?function\s*\w*\s*\(|(const|let|var)\s+\w+\s*=|#include\s*<|package\s+[\w.]+;|def\s+\w+\s*\(|<\?php)/m;
  if (codePattern.test(trimmed)) {
    return true;
  }

  return false;
};

/**
 * Extracts and formats code or HTML for preview in the chat listing,
 * matching Telegram's single-line preview behavior:
 * - Flattens lines into a single space-separated line
 * - Uses the full available width of the chat item
 * - Truncates cleanly with ellipsis via CSS
 * - Strips code fences and solitary quote markers
 */
export const formatCodeOrHtmlPreview = (text: string): string => {
  if (!text || typeof text !== 'string') return '';
  const rawLines = text.split(/\r?\n/);

  const cleanLines: string[] = [];
  for (const rawLine of rawLines) {
    const trimmed = rawLine.trim();
    if (!trimmed) continue;
    // Skip markdown code fence line (e.g. ```html or ```)
    if (trimmed.startsWith('```') && trimmed.length <= 20 && !trimmed.slice(3).includes('`')) {
      continue;
    }
    // Skip solitary quote characters '>' or '>>'
    if (trimmed === '>' || trimmed === '>>') {
      continue;
    }
    cleanLines.push(trimmed);
    // Gather enough content to fill the full preview width without loading excessive lines into memory
    if (cleanLines.join(' ').length >= 300) {
      break;
    }
  }

  if (cleanLines.length === 0) {
    const fallback = text.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
    return fallback.slice(0, 300);
  }

  return cleanLines.join(' ').replace(/\s+/g, ' ').trim();
};

